/**
 * Product Gallery — desktop + mobile slider, thumbnail sync, variant galleries.
 */
import { Component } from '@theme/component';

class ProductGalleryComponent extends Component {
  _variantImages = null;

  connectedCallback() {
    super.connectedCallback();
    this.#initMobileThumbs();
    this.#initDesktopThumbs();
    this.#markAiImage();
  }

  /**
   * Mention IA : la 3e image de chaque galerie porte un badge (CSS, base.css) ;
   * son texte alternatif reçoit la même mention pour les lecteurs d'écran.
   */
  #markAiImage() {
    const suffix = (typeof Theme !== 'undefined' && Theme.translations?.ai_generated_image) || '';
    if (!suffix) return;
    this.querySelectorAll('.js-slider-track, .product-gallery__grid-a, .product-gallery__grid-b').forEach((list) => {
      const items = [...list.children].filter((el) => !el.classList.contains('cv-slider__clone'));
      this.#appendAiAlt(items[2]?.querySelector('img'), suffix);
    });
    this.querySelectorAll('.product-gallery__thumb[data-index="2"] img, .product-gallery__thumb[data-mobile-thumb="2"] img').forEach((img) =>
      this.#appendAiAlt(img, suffix)
    );
  }

  #appendAiAlt(img, suffix) {
    if (!img || img.dataset.aiAlt || img.alt.includes(suffix)) return;
    img.alt = img.alt ? `${img.alt} – ${suffix}` : suffix;
    img.dataset.aiAlt = 'true';
  }

  /**
   * Desktop: bind thumbnail clicks + swap thumbs on slide change (like mobile).
   */
  #initDesktopThumbs() {
    const desktop = this.querySelector('.product-gallery__desktop');
    if (!desktop) return;

    const mainMedia = desktop.querySelector('[data-main-media]');
    if (!mainMedia) return;

    const slider = mainMedia.querySelector('.js-cv-slider');
    const thumbContainer = desktop.querySelector('.tw\\:grid');

    if (!slider || !thumbContainer) return;

    const track = slider.querySelector('.js-slider-track');
    if (!track) return;

    // Thumbnail click → goTo that slide index
    thumbContainer.addEventListener('click', (e) => {
      const btn = e.target.closest('.product-gallery__thumb');
      if (!btn || !slider.goTo) return;
      const idx = parseInt(btn.dataset.index, 10);
      slider.goTo(idx);
    });

    // On slide change → swap the clicked thumb's image with the previous slide's image
    slider.addEventListener('slide-change', (e) => {
      const { index: newIndex, previousIndex } = e.detail;
      this.#swapDesktopThumb(thumbContainer, track, newIndex, previousIndex);
    });
  }

  /**
   * Desktop thumb rotation: remove the clicked thumb, push the old main
   * image to the end of the thumbnail row (slide effect).
   */
  #swapDesktopThumb(thumbContainer, track, newIndex, previousIndex) {
    const clickedThumb = thumbContainer.querySelector(`[data-index="${newIndex}"]`);
    if (!clickedThumb) return;

    const prevSlide = track.children[previousIndex];
    if (!prevSlide) return;
    const prevImg = prevSlide.querySelector('img');
    if (!prevImg) return;

    // Build a new thumb from the old main image
    const newThumb = clickedThumb.cloneNode(true);
    const newThumbImg = newThumb.querySelector('img');
    if (newThumbImg) {
      newThumbImg.src = prevImg.src;
      newThumbImg.srcset = prevImg.srcset || '';
      newThumbImg.alt = prevImg.alt || '';
    }
    newThumb.dataset.index = String(previousIndex);
    const prevMediaId = prevSlide.dataset.slideMediaId || '';
    newThumb.dataset.mediaId = prevMediaId;
    delete newThumb.dataset.zoomSrc;

    // Remove clicked thumb, append old main at the end
    clickedThumb.remove();
    thumbContainer.appendChild(newThumb);
  }

  #initMobileThumbs() {
    const mobileSection = this.querySelector('.hidden--desktop');
    if (!mobileSection) return;

    const slider = mobileSection.querySelector('.js-cv-slider');
    const thumbContainer = mobileSection.querySelector('.product-gallery__mobile-thumbs');
    if (!slider || !thumbContainer) return;

    const track = slider.querySelector('.js-slider-track');
    if (!track) return;

    thumbContainer.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-mobile-thumb]');
      if (!btn || !slider.goTo) return;
      const idx = parseInt(btn.dataset.mobileThumb, 10);
      slider.goTo(idx);
    });

    slider.addEventListener('slide-change', (e) => {
      const { index: newIndex, previousIndex } = e.detail;
      this.#swapMobileThumb(thumbContainer, track, newIndex, previousIndex);
    });
  }

  #swapMobileThumb(thumbContainer, track, newIndex, previousIndex) {
    const clickedThumb = thumbContainer.querySelector(`[data-mobile-thumb="${newIndex}"]`);
    if (!clickedThumb) return;

    const prevSlide = track.children[previousIndex];
    if (!prevSlide) return;
    const prevImg = prevSlide.querySelector('img');
    if (!prevImg) return;

    // Build a new thumb from the old main image
    const newThumb = clickedThumb.cloneNode(true);
    const newThumbImg = newThumb.querySelector('img');
    if (newThumbImg) {
      newThumbImg.src = prevImg.src;
      newThumbImg.srcset = prevImg.srcset || '';
      newThumbImg.alt = prevImg.alt || '';
    }
    newThumb.dataset.mobileThumb = String(previousIndex);

    // Remove clicked thumb, append old main at the end
    clickedThumb.remove();
    thumbContainer.appendChild(newThumb);
  }

  /**
   * Public: called by variant selector to navigate to a media.
   */
  selectMediaById(mediaId) {
    const mainContainer = this.querySelector('.product-gallery__desktop [data-main-media]');
    if (!mainContainer) return;

    const slider = mainContainer.querySelector('.js-cv-slider');
    if (slider && slider.goTo) {
      const track = slider.querySelector('.js-slider-track');
      if (track) {
        for (let i = 0; i < track.children.length; i++) {
          if (track.children[i].dataset.slideMediaId === mediaId) {
            slider.goTo(i);
            mainContainer.dataset.currentMediaId = mediaId;
            this.#updateActiveStates(mediaId);
            return;
          }
        }
      }
    }

    // Fallback: variant image mode
    if (this._variantImages && mediaId.startsWith('variant-img-')) {
      const idx = parseInt(mediaId.replace('variant-img-', ''), 10);
      const img = this._variantImages[idx];
      if (!img) return;
      this.#updateActiveStates(mediaId);
      this.#swapMainImage(mainContainer, img.src_800, img.srcset, img.alt || '', img.zoom);
      mainContainer.dataset.currentMediaId = mediaId;
    }
  }

  selectMedia(event) {
    const dot = event.target.closest('[data-media-id]');
    if (dot) this.selectMediaById(dot.dataset.mediaId);
  }

  #updateActiveStates(mediaId) {
    this.querySelectorAll('[data-gallery-dots] button').forEach((dot) => {
      if (dot.dataset.mediaId === mediaId) {
        dot.classList.remove('tw:opacity-50');
      } else {
        dot.classList.add('tw:opacity-50');
      }
    });
  }

  #swapMainImage(mainContainer, src, srcset, alt, zoomSrc) {
    const mainImg = mainContainer.querySelector('img');
    if (mainImg) {
      mainImg.style.opacity = '0';
      mainImg.src = src;
      mainImg.srcset = srcset;
      if (alt) mainImg.alt = alt;
      mainImg.onload = () => { mainImg.style.opacity = '1'; };
      setTimeout(() => { mainImg.style.opacity = '1'; }, 150);
    }
    mainContainer.dataset.zoomSrc = zoomSrc;
  }

  /**
   * Replace the gallery with the variant metafield images and rebuild the cv-slider.
   */
  replaceGallery(images) {
    if (!images || images.length === 0) return;

    this._variantImages = images;

    // ── Desktop: rebuild the cv-slider so animations match standard products ──
    const desktopContainer = this.querySelector('.product-gallery__desktop');
    const mainContainer = desktopContainer?.querySelector('[data-main-media]');
    const oldSlider = mainContainer?.querySelector('.js-cv-slider');

    if (mainContainer && oldSlider) {
      // Fresh slider element (no stale drag/click listeners) — one slide per image
      const newSlider = document.createElement('div');
      newSlider.className = 'cv-slider js-cv-slider';
      newSlider.style.cssText = '--spv-desktop: 1; --spv-mobile: 1; --gap-slide: 0px;';
      newSlider.setAttribute('role', 'region');
      newSlider.setAttribute('aria-label', 'Slider');
      newSlider.setAttribute('tabindex', '0');
      newSlider.dataset.autoplay = '0';
      newSlider.dataset.loop = 'false';

      const newTrack = document.createElement('div');
      newTrack.className = 'cv-slider__track js-slider-track';
      images.forEach((img, i) => {
        const slide = document.createElement('div');
        slide.dataset.slideMediaId = `variant-img-${i}`;
        slide.dataset.index = String(i);
        slide.setAttribute('data-zoom-trigger', '');
        slide.dataset.zoomSrc = img.zoom;
        slide.innerHTML = `<img src="${img.src_800}" srcset="${img.srcset}" alt="${img.alt || ''}" loading="${i === 0 ? 'eager' : 'lazy'}" class="tw:w-full tw:h-full tw:object-cover" sizes="(min-width: 750px) 55vw, 100vw" style="aspect-ratio: var(--gallery-ratio, 1/1);">`;
        newTrack.appendChild(slide);
      });
      newSlider.appendChild(newTrack);
      oldSlider.replaceWith(newSlider);
      mainContainer.dataset.currentMediaId = 'variant-img-0';

      // Rebuild thumbnails on a fresh element (avoids stacking click listeners)
      const oldThumbGrid = desktopContainer.querySelector('.tw\\:grid');
      if (oldThumbGrid) {
        const freshGrid = oldThumbGrid.cloneNode(false);
        if (images.length > 1) {
          // Mêmes règles que product-gallery.liquid : 3 colonnes fixes en « thumbnail_slider »,
          // sinon une colonne par miniature avec un ratio selon leur nombre.
          const thumbCount = images.length - 1;
          let thumbRatio = '237/178';
          if (this.dataset.layout === 'thumbnail_slider') {
            freshGrid.style.gridTemplateColumns = 'repeat(3, 1fr)';
          } else {
            freshGrid.style.gridTemplateColumns = `repeat(${thumbCount}, 1fr)`;
            if (thumbCount >= 4) thumbRatio = '1/1';
            else if (thumbCount === 2) thumbRatio = '358/180';
          }
          for (let i = 1; i < images.length; i++) {
            const img = images[i];
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = `product-gallery__thumb${i === 1 ? ' is-active' : ''} tw:overflow-hidden tw:cursor-pointer tw:border-none tw:p-0 tw:bg-transparent`;
            btn.style.aspectRatio = thumbRatio;
            btn.dataset.mediaId = `variant-img-${i}`;
            btn.dataset.index = String(i);
            btn.setAttribute('on:click', '/selectMedia');
            btn.innerHTML = `<img src="${img.src_400}" alt="${img.alt || ''}" loading="lazy" class="tw:w-full tw:h-full tw:object-cover">`;
            freshGrid.appendChild(btn);
          }
        } else {
          freshGrid.style.gridTemplateColumns = '';
        }
        oldThumbGrid.replaceWith(freshGrid);
      }

      // Rebuild dots — routed through selectMedia (slider goTo) like standard mode
      const dotsContainer = desktopContainer.querySelector('[data-gallery-dots]');
      if (dotsContainer) {
        dotsContainer.innerHTML = '';
        if (images.length > 1) {
          images.forEach((img, i) => {
            const dot = document.createElement('button');
            dot.type = 'button';
            dot.className = `tw:w-2 tw:h-2 tw:rounded-full tw:border-none tw:p-0 tw:cursor-pointer tw:transition-colors tw:bg-muted${i !== 0 ? ' tw:opacity-50' : ''}`;
            dot.dataset.dotIndex = String(i);
            dot.dataset.mediaId = `variant-img-${i}`;
            dot.dataset.zoomSrc = img.zoom;
            dot.setAttribute('aria-label', `Image ${i + 1}`);
            dot.setAttribute('on:click', '/selectMedia');
            dotsContainer.appendChild(dot);
          });
        }
      }

      // Boot the slider engine, then (re)wire thumbnail rotation on the fresh nodes
      if (window.initCvSliders) window.initCvSliders();
      this.#initDesktopThumbs();
    }

    // Rebuild mobile
    const mobileContainer = this.querySelector('.hidden--desktop');
    if (mobileContainer) {
      const track = mobileContainer.querySelector('.js-slider-track');
      if (track) {
        track.innerHTML = '';
        images.forEach((img) => {
          const slide = document.createElement('div');
          slide.className = 'product-gallery__media';
          slide.style.aspectRatio = '390/593';
          slide.innerHTML = `<img src="${img.src_800}" srcset="${img.srcset}" alt="${img.alt || ''}" loading="lazy" class="tw:w-full tw:h-full tw:object-cover" sizes="100vw">`;
          track.appendChild(slide);
        });
      }

      let thumbContainer = mobileContainer.querySelector('.product-gallery__mobile-thumbs');
      if (thumbContainer && images.length > 1) {
        const thumbImages = images.slice(1);
        thumbContainer.style.gridTemplateColumns = `repeat(${thumbImages.length}, 1fr)`;
        thumbContainer.innerHTML = '';
        thumbImages.forEach((img, i) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'product-gallery__thumb tw:overflow-hidden tw:cursor-pointer tw:border-none tw:p-0 tw:bg-transparent';
          btn.style.aspectRatio = '127.33/96.57';
          btn.dataset.mobileThumb = String(i + 1);
          btn.innerHTML = `<img src="${img.src_400}" alt="${img.alt || ''}" loading="lazy" class="tw:w-full tw:h-full tw:object-cover">`;
          thumbContainer.appendChild(btn);
        });
      } else if (thumbContainer) {
        thumbContainer.innerHTML = '';
      }

      const mobileSlider = mobileContainer.querySelector('.js-cv-slider');
      if (mobileSlider) {
        mobileSlider.classList.remove('is-initialized');
        if (window.initCvSliders) window.initCvSliders();
      }
      this.#initMobileThumbs();
    }

    this.#markAiImage();
  }
}

if (!customElements.get('product-gallery-component')) {
  customElements.define('product-gallery-component', ProductGalleryComponent);
}
